import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Table, ConfigProvider } from 'antd';
import { User } from '../../../types/User';
import { Group } from '../../../types/Group';
import { getInitials, getGroupNames, getRoleLabel } from '../../../utils/userUtils';
import Tag from '../../../utils/shared/components/Tags/Tag';
import './style/UsersTable.css';

interface UsersTableProps {
    users: User[];
    allGroups: Group[];
    selectedRowKeys: React.Key[];
    onSelectChange: (selectedRowKeys: React.Key[]) => void;
    onRowClick: (user: User) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({ users, allGroups, selectedRowKeys, onSelectChange, onRowClick }) => {
    const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
    const [sortField, setSortField] = useState<'name' | 'login' | 'role' | 'groups' | null>(null);
    const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);

    useEffect(() => {
        if (selectedRowKeys.length > 0) {
            const lastKey = selectedRowKeys[selectedRowKeys.length - 1];
            const ref = rowRefs.current[lastKey as string];
            if (ref) {
                ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedRowKeys]);

    const sortedUsers = useMemo(() => {
        if (!sortField || !sortOrder) return users;

        return [...users].sort((a, b) => {
            let valueA: string;
            let valueB: string;

            if (sortField === 'role') {
                valueA = a.isAdmin ? 'администратор' : 'оператор';
                valueB = b.isAdmin ? 'администратор' : 'оператор';
            } else if (sortField === 'groups') {
                valueA = getGroupNames(a.groups, allGroups).toLowerCase();
                valueB = getGroupNames(b.groups, allGroups).toLowerCase();
            } else {
                valueA = a[sortField].toLowerCase();
                valueB = b[sortField].toLowerCase();
            }

            if (sortOrder === 'ascend') {
                return valueA.localeCompare(valueB);
            } else {
                return valueB.localeCompare(valueA);
            }
        });
    }, [users, sortField, sortOrder, allGroups]);

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter.columnKey === 'name' || sorter.columnKey === 'login' || sorter.columnKey === 'role' || sorter.columnKey === 'groups') {
            if (sorter.order === 'ascend') {
                setSortField(sorter.columnKey);
                setSortOrder('ascend');
            } else if (sorter.order === 'descend') {
                setSortField(sorter.columnKey);
                setSortOrder('descend');
            } else {
                setSortField(null);
                setSortOrder(null);
            }
        }
    };

    const columns = [
        {
            title: 'Пользователь',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: sortField === 'name' ? sortOrder : null,
            render: (text: string, record: User) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="user-initials-users">{getInitials(record.name)}</span>
                    {text}
                </span>
            ),
            width: 680,
        },
        {
            title: 'Логин',
            dataIndex: 'login',
            key: 'login',
            sorter: true,
            sortOrder: sortField === 'login' ? sortOrder : null,
            width: 240,
        },
        {
            title: 'Группа',
            dataIndex: 'groups',
            key: 'groups',
            sorter: true,
            sortOrder: sortField === 'groups' ? sortOrder : null,
            width: 680,
            render: (groups: (string | { uid: string })[]) => getGroupNames(groups, allGroups),
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            sorter: true,
            sortOrder: sortField === 'role' ? sortOrder : null,
            width: 240,
            render: (_: unknown, record: User) => (
                <Tag state={record.isAdmin ? 'processing' : 'default'} border={true}>
                    {getRoleLabel(record) === 'администратор' ? 'Администратор' : 'Оператор'}
                </Tag>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    return (
        <ConfigProvider theme={{ components: { Table: { headerBorderRadius: 0 } } }}>
            <Table
                className="table users-table-sort"
                columns={columns}
                dataSource={sortedUsers}
                pagination={false}
                rowKey="uid"
                rowSelection={rowSelection}
                onChange={handleTableChange}
                showSorterTooltip={false}
                onRow={(record) => ({
                    ref: (node: HTMLTableRowElement) => {
                        rowRefs.current[record.uid] = node;
                    },
                    onClick: () => onRowClick(record),
                })}
            />
        </ConfigProvider>
    );
};

export default UsersTable; 